import { getAccessToken, getScopeHeaders, setScope } from '../lib/auth-storage';
import { asArray, unwrapEnvelopeData } from './client';

const API_BASE = import.meta.env.VITE_WORKSPACE_API ?? '/api/v1';

export type WorkspaceStatus = 'pending' | 'active' | 'suspended' | 'archived' | 'deleting';
export type WorkspaceType = 'brand' | 'department' | 'regional' | 'team' | 'marketplace' | 'ai' | 'reporting' | 'general';

export type Workspace = {
  id: string;
  tenant_id: string;
  organization_id: string;
  slug: string;
  code?: string;
  name: string;
  type?: WorkspaceType;
  owner_id?: string;
  status: WorkspaceStatus;
  description?: string;
  metadata?: Record<string, string>;
  settings?: Record<string, string>;
  configuration?: Record<string, string>;
  feature_flags?: Record<string, boolean>;
  branding?: { logo_url?: string; primary_color?: string; display_name?: string; theme?: string };
  profile?: { timezone?: string; locale?: string; currency?: string };
  timezone?: string;
  locale?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
};

export type SettingsBundle = {
  settings: Record<string, string>;
  configuration: Record<string, string>;
  preferences: { notifications_enabled: boolean; dashboard_layout?: string };
  feature_flags: Record<string, boolean>;
};

export type ActivityEntry = { id: string; action: string; summary: string; created_at: string };
export type AuditEntry = { id: string; action: string; outcome: string; created_at: string };
export type Dashboard = { widgets: { id: string; title: string; type: string; order: number }[]; stats?: Record<string, unknown> };
export type Analytics = { metrics?: Record<string, number>; kpis?: Record<string, unknown>; period?: string; generated_at?: string };
export type SwitchResult = { workspace_id: string; tenant_id: string; organization_id: string; slug: string; name: string; status: WorkspaceStatus };

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function wsFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Workspaces ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return unwrapEnvelopeData<T>(body);
}

export const workspaceApi = {
  list: async (params?: { status?: string; q?: string; owner_id?: string; type?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.owner_id) qs.set('owner_id', params.owner_id);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    // Contract: list is always an array (never null / envelope).
    return asArray<Workspace>(await wsFetch(`/workspaces${query ? `?${query}` : ''}`));
  },
  search: (params?: Record<string, string>) => wsFetch<{ results: Workspace[]; count: number }>(`/workspaces/search?${new URLSearchParams(params).toString()}`),
  get: (id: string) => wsFetch<Workspace>(`/workspaces/${id}`),
  create: (input: Partial<Workspace> & { name: string; activate?: boolean }) =>
    wsFetch<Workspace>('/workspaces', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Workspace>) =>
    wsFetch<Workspace>(`/workspaces/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  activate: (id: string) => wsFetch<Workspace>(`/workspaces/${id}/activate`, { method: 'POST' }),
  suspend: (id: string) => wsFetch<Workspace>(`/workspaces/${id}/suspend`, { method: 'POST' }),
  archive: (id: string) => wsFetch<Workspace>(`/workspaces/${id}/archive`, { method: 'POST' }),
  restore: (id: string) => wsFetch<Workspace>(`/workspaces/${id}/restore`, { method: 'POST' }),
  clone: (id: string, input: { name: string; slug?: string }) =>
    wsFetch<Workspace>(`/workspaces/${id}/clone`, { method: 'POST', body: JSON.stringify(input) }),
  switch: async (id: string): Promise<SwitchResult> => {
    const result = await wsFetch<SwitchResult>(`/workspaces/${id}/switch`, { method: 'POST' });
    setScope(result.tenant_id, result.organization_id, undefined, result.workspace_id);
    return result;
  },
  getSettings: (id: string) => wsFetch<SettingsBundle>(`/workspaces/${id}/settings`),
  updateSettings: (id: string, input: Partial<SettingsBundle>) =>
    wsFetch<SettingsBundle>(`/workspaces/${id}/settings`, { method: 'PUT', body: JSON.stringify(input) }),
  getActivity: async (id: string) => asArray<ActivityEntry>(await wsFetch(`/workspaces/${id}/activity`)),
  getAudit: async (id: string) => asArray<AuditEntry>(await wsFetch(`/workspaces/${id}/audit`)),
  getDashboard: (id: string) => wsFetch<Dashboard>(`/workspaces/${id}/dashboard`),
  getAnalytics: (id: string) => wsFetch<Analytics>(`/workspaces/${id}/analytics`),
};
